import { criterion, fromCriteria, unsupported } from "@/core/check";
import type { Lesson } from "@/core/types";
import { countTrackedChanges, isTrackingOn, readChangeTrackingMode, readComments } from "@/office/word/review";

export const lektionZusammenarbeitAbschluss: Lesson = {
  id: "abschlussuebung",
  title: "Abschlussübung: Eine ganze Überarbeitungsrunde",
  summary: "Den kompletten Ablauf einmal von Anfang bis Ende durchspielen.",
  durationMinutes: 12,
  learningGoals: [
    "Eine Überarbeitungsrunde vollständig durchführen",
    "Kommentare und Änderungen sauber abschließen",
  ],
  steps: [
    {
      id: "runde-durchspielen",
      title: "Die Runde durchspielen",
      explanation: [
        "Zum Abschluss spielst du beide Rollen nacheinander: erst die gegenlesende " +
          "Person, dann die verfassende.",
        "Als Gegenlesende schaltest du die Nachverfolgung ein, änderst etwas am Text und " +
          "hinterlässt einen Kommentar zu einer Stelle, über die du nicht allein " +
          "entscheiden willst. Als Verfassende gehst du danach beides durch: Änderung " +
          "annehmen oder ablehnen, Kommentar beantworten und auflösen.",
        "Am Ende soll das Dokument versandfertig sein – so wie es in einem Unternehmen " +
          "tatsächlich rausgehen würde.",
      ],
      keyPoints: [
        "Runde 1: Nachverfolgung ein, ändern, kommentieren.",
        "Runde 2: entscheiden, antworten, auflösen.",
        "Zum Schluss: Nachverfolgung aus und Endkontrolle.",
      ],
      task: {
        instruction:
          "Spiele die Runde komplett durch. Am Ende muss gelten: mindestens ein " +
          "aufgelöster Kommentar mit Antwort, keine offenen nachverfolgten Änderungen und " +
          "die Nachverfolgung wieder ausgeschaltet.",
        hint: "Arbeite die Kriterienliste von unten nach oben ab – die Nachverfolgung schaltest du ganz zum Schluss aus.",
        check: async () => {
          const modus = await readChangeTrackingMode();
          const kommentare = await readComments();
          const offen = await countTrackedChanges();

          if (modus === null && kommentare === null) {
            return unsupported(
              "Diese Word-Version stellt dem Add-in weder Kommentare noch den Zustand der " +
                "Änderungsnachverfolgung zur Verfügung, deshalb lässt sich die Abschlussübung " +
                "hier nicht automatisch prüfen.",
              "Gehe die drei Punkte aus der Aufgabe selbst durch und markiere den Schritt danach als erledigt.",
            );
          }

          const kriterien = [];
          if (kommentare !== null) {
            const mitAntwort = kommentare.filter((kommentar) => kommentar.replyCount > 0);
            const aufgeloest = kommentare.filter((kommentar) => kommentar.resolved);
            kriterien.push(
              criterion(
                `Mindestens ein Kommentar wurde beantwortet (gefunden: ${mitAntwort.length})`,
                mitAntwort.length >= 1,
              ),
              criterion(
                `Mindestens ein Kommentar ist aufgelöst (gefunden: ${aufgeloest.length})`,
                aufgeloest.length >= 1,
              ),
            );
          }
          if (offen !== null) {
            kriterien.push(
              criterion(`Keine offenen nachverfolgten Änderungen (gefunden: ${offen})`, offen === 0),
            );
          }
          if (modus !== null) {
            kriterien.push(
              criterion(
                `Die Nachverfolgung ist ausgeschaltet (Zustand: ${modus})`,
                !isTrackingOn(modus),
              ),
            );
          }

          return fromCriteria(kriterien, {
            success:
              "Stark – du hast eine vollständige Überarbeitungsrunde sauber abgeschlossen.",
            failure: "Die Runde ist noch nicht ganz durch:",
            hint: "Ein aufgelöster Kommentar bleibt sichtbar, wird aber ausgegraut. Gelöschte Kommentare zählen hier nicht mit.",
          });
        },
      },
    },
    {
      id: "rueckblick",
      title: "Rückblick",
      explanation: [
        "Damit ist der Kurs an dem Punkt angekommen, an dem Word im Arbeitsalltag " +
          "tatsächlich benutzt wird: nicht allein, sondern zu mehreren an einem Dokument.",
        "Die wichtigste Erkenntnis aus diesem Modul ist eine Warnung: Die " +
          "Markup-Anzeige täuscht. Ein Dokument, das sauber aussieht, kann voller " +
          "aufgezeichneter Änderungen und interner Kommentare stecken. Der Blick in die " +
          "Dokumentprüfung vor dem Versand kostet zwanzig Sekunden und hat schon manche " +
          "unangenehme Rückfrage verhindert.",
      ],
      keyPoints: [
        "Kommentar für Rückfragen, Nachverfolgung für konkrete Textänderungen.",
        "„Kein Markup“ verbirgt Änderungen nur – es entfernt sie nicht.",
        "Erst alle Änderungen entscheiden, dann die Nachverfolgung ausschalten.",
        "Vor der Weitergabe: Dokumentprüfung, im Zweifel als PDF versenden.",
      ],
    },
  ],
};
